import {Injectable} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {LayerUtilService} from './layer-util.service';
import {SpatialUtilService} from '../services/spatial-util.service';
import {LookupService} from '../../common/service/lookup.service';
import {LineSymbologyLookup} from '../../common/models/line-symbology.model';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
    providedIn: 'root'
})
export class PolylineLayerService {
    private readonly layerId = 'polylineFeatureLayer_layer';

    private readonly layerSelector = `#${this.layerId}`;

    private readonly featureClass = 'polyline';

    private readonly featureIdAttrName = this.layerUtilService.getFeatureIdAttrName(this.featureClass);

    private readonly featureTypeAttrName = this.layerUtilService.getFeatureTypeAttrName(this.featureClass);

    private readonly covenantLineType = 24;

    private readonly totalLotBoundaryLineType = 95;

    private readonly polylineTypes = [this.covenantLineType, this.totalLotBoundaryLineType];

    private readonly getLineSymbologyLookups = (): LineSymbologyLookup[] =>
        this.lookupService.getLookups('lineSymbology').filter((l: LineSymbologyLookup) => this.polylineTypes.includes(l.type));

    private svgStyle: string;

    private trustedSvgStyle: SafeHtml;

    constructor(
        private domSanitizer: DomSanitizer,
        private layerUtilService: LayerUtilService,
        private lookupService: LookupService,
        private spatialUtilService: SpatialUtilService
    ) {}

    getSvgStyle(): SafeHtml {
        const scaleFactor = this.layerUtilService.getPixelScaleFactor();
        if (scaleFactor === 0) {
            return '';
        }

        const mapBackgroundColor = this.spatialUtilService.getMapBackground();

        const showFeatures = this.layerUtilService.showFeatures();

        const styles = [
            `${this.layerSelector} > path[${this.featureTypeAttrName}][data-selected] {stroke:${this.layerUtilService.getSelectedColor()};}`,

            `${this.layerSelector} > path[${this.featureTypeAttrName}][data-hidden]:not([data-selected])` +
                ` {stroke:${this.layerUtilService.getHiddenColor()};}`,

            `${this.layerSelector} > path[${this.featureTypeAttrName}]:hover` +
                ` {animation: feature-symbol-hover 0.3s alternate ease-in-out none infinite;}`,

            `${this.layerSelector} > path[${this.featureTypeAttrName}]:not([data-hidden]):not([data-selected]):hover` +
                ` {stroke:${this.layerUtilService.getHoverColor()};}`,

            `${this.layerSelector} > path[${this.featureTypeAttrName}="${this.totalLotBoundaryLineType}"]:not([data-selected]) {stroke-opacity:0.5;}`,
            ...this.getLineSymbologyLookups()
                .filter(l => l.viewMode.toLowerCase() === mapBackgroundColor)
                .map(l => {
                    const stroke = showFeatures ? `rgb(${l.color})` : 'transparent';
                    const strokeWidth = l.width * scaleFactor;
                    const strokeLineCap = l.lineCap;
                    const strokeLineJoin = l.lineJoin;

                    let strokeDashArray: string;
                    if (l.dasharray) {
                        const delimiter = parseFloat(l.delimiter);
                        const dashValueMultiplier = scaleFactor * delimiter;
                        strokeDashArray = l.dasharray
                            .split(',')
                            .map(v => `${parseFloat(v) * dashValueMultiplier}`)
                            .join();
                    } else {
                        strokeDashArray = 'none';
                    }

                    const lineProperties = [
                        `stroke:${stroke};`,
                        `stroke-linecap:${strokeLineCap};`,
                        `stroke-linejoin:${strokeLineJoin};`,
                        `stroke-miterlimit:1;`,
                        `stroke-width: ${strokeWidth};`,
                        `stroke-opacity:1.0;`,
                        `fill-opacity:1.0;`,
                        `stroke-dasharray: ${strokeDashArray};`
                    ];

                    return `${this.layerSelector} path[${this.featureTypeAttrName}="${l.type}"] {\n${lineProperties.join('\n')}\n}`;
                })
        ];

        const newSvgStyle = styles.join('\n');
        if (newSvgStyle === this.svgStyle) {
            return this.trustedSvgStyle;
        }

        this.svgStyle = newSvgStyle;

        this.trustedSvgStyle = this.domSanitizer.bypassSecurityTrustHtml(this.svgStyle);

        return this.trustedSvgStyle;
    }

    init() {
        const polylineFeatureLayer = this.spatialUtilService.getPolylineMapLayer();

        this.layerUtilService.monkeyPatchFeatureLayerDrawShapeForPolylines(polylineFeatureLayer);

        this.layerUtilService.addLayerEventHandler(polylineFeatureLayer, 'graphic-node-add', (evt: {graphic: any; node: any}) => {
            const featureId = evt.graphic.attributes.id;

            document
                .querySelectorAll(`${this.layerSelector} path[${this.featureIdAttrName}="${featureId}"]`)
                .forEach(path => path.parentNode.removeChild(path));

            evt.node.setAttribute('id', this.layerUtilService.getFeatureNodeId(this.featureClass, featureId));
            evt.node.setAttribute(this.featureTypeAttrName, `${evt.graphic.attributes.type}`);
            evt.node.setAttribute(this.featureIdAttrName, `${featureId}`);

            this.layerUtilService.setSelectedAttr(evt.node, this.featureClass, featureId);
        });

        this.layerUtilService.addLayerEventHandler(polylineFeatureLayer, 'graphic-draw', (evt: any) => {
            const featureId = evt.graphic.attributes.id;

            this.layerUtilService.setHiddenAttr(evt.node, this.featureClass, featureId);
        });

        this.layerUtilService.addLayerEventHandler(polylineFeatureLayer, 'edits-complete', () => {
            const lineIds = this.spatialUtilService.getPolylineGraphics().map((g: any) => g.attributes.id);

            document.querySelectorAll(`${this.layerSelector} path[${this.featureIdAttrName}]`).forEach(path => {
                const lineId = parseInt(path.getAttribute(this.featureIdAttrName));
                if (!lineIds.includes(lineId)) {
                    path.parentNode.removeChild(path);
                }
            });
        });

        this.layerUtilService.addLayerEventHandler(polylineFeatureLayer, 'mouse-over', (evt: any) => {
            this.layerUtilService.onMouseOver(this.featureClass, evt);
        });

        this.layerUtilService.addLayerEventHandler(polylineFeatureLayer, 'mouse-out', (evt: any) => {
            this.layerUtilService.onMouseOut(this.featureClass, evt);
        });

        return polylineFeatureLayer;
    }
}
